import { useField, useFormikContext } from "formik";
import React, { ChangeEvent, useCallback, useEffect } from "react";

interface SkontoProps {
  bruttoBetrag: string;
  rechnungsBetragChanged: boolean;
  isAbweichenderRechnungsBetrag: boolean;
  skontoBetrag: string;
  zahlBetrag: string;
}

export const useSkontoBerechnung = ({
  bruttoBetrag,
  rechnungsBetragChanged,
  isAbweichenderRechnungsBetrag,
  skontoBetrag,
  zahlBetrag,
}: SkontoProps) => {
  const { setFieldValue } = useFormikContext();
  const [skontoInProzentField] = useField("skonto1InProzent");
  const [skontoBetragField] = useField("skontoBetrag");
  const [rechnungsbetragField] = useField("rechnungsBetrag");
  const [bruttobetragField] = useField("bruttobetrag");
  const [faelligkeitMitSkontoField] = useField("faelligkeitMitSkonto");
  const [bezahltAmField] = useField("bezahltAm");

  const formatNumber = (num: number) =>
    new Intl.NumberFormat("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);

  const parseNumberString = useCallback((value: string): number => {
    return parseFloat(value.replace(".", "").replace(",", "."));
  }, []);

  const calculateZahlbetrag = useCallback(
    (skontoBetrag: number, isAbweichend: boolean) => {
      const aktuellesDatum = new Date();
      const faelligkeit = new Date(
        faelligkeitMitSkontoField.value + "T23:59:59.999Z"
      );
      const betrag = isAbweichend
        ? parseNumberString(rechnungsbetragField.value)
        : parseNumberString(bruttoBetrag);

      const isBeforeSkontoDeadline = bezahltAmField.value
        ? new Date(bezahltAmField.value) <= faelligkeit
        : aktuellesDatum <= faelligkeit;

      const zahlbetrag =
        isBeforeSkontoDeadline && !isNaN(skontoBetrag)
          ? betrag - skontoBetrag
          : betrag;

      setFieldValue("zahlbetrag", formatNumber(zahlbetrag));
      if (isBeforeSkontoDeadline) {
        setFieldValue("skontoInEuro", formatNumber(betrag));
      }
    },
    [
      bezahltAmField.value,
      faelligkeitMitSkontoField.value,
      rechnungsbetragField.value,
      bruttoBetrag,
      setFieldValue,
      parseNumberString,
    ]
  );

  const calculateZahlbetragWithSkontoBetrag = useCallback(() => {
    const skontoBetrag = parseNumberString(skontoBetragField.value);
    calculateZahlbetrag(skontoBetrag, isAbweichenderRechnungsBetrag);
  }, [
    skontoBetragField.value,
    isAbweichenderRechnungsBetrag,
    calculateZahlbetrag,
    parseNumberString,
  ]);

  const calculateSkontoBetrag = useCallback(
    (skontoProzent: string): number => {
      const betrag = isAbweichenderRechnungsBetrag
        ? parseNumberString(rechnungsbetragField.value)
        : parseNumberString(bruttoBetrag);

      const skonto =
        Math.round(((betrag * parseFloat(skontoProzent)) / 100) * 100) / 100;

      if (isNaN(skonto)) {
        setFieldValue("skontoBetrag", "");
        return 0;
      }

      setFieldValue("skontoBetrag", formatNumber(skonto));
      return skonto;
    },
    [
      isAbweichenderRechnungsBetrag,
      rechnungsbetragField.value,
      bruttoBetrag,
      setFieldValue,
      parseNumberString,
    ]
  );

  const calculateSkontoFromSkontoInProzent = useCallback(() => {
    const skontoProzent = skontoInProzentField.value;
    const skontoBetrag = calculateSkontoBetrag(skontoProzent);
    calculateZahlbetrag(skontoBetrag, isAbweichenderRechnungsBetrag);
  }, [
    skontoInProzentField.value,
    calculateSkontoBetrag,
    calculateZahlbetrag,
    isAbweichenderRechnungsBetrag,
  ]);

  const handleSkontoOrZahlbetragCalculation = useCallback(() => {
    if (skontoInProzentField.value) {
      if (!skontoBetragField.value) {
        calculateSkontoFromSkontoInProzent();
      } else {
        calculateZahlbetragWithSkontoBetrag();
      }
    } else {
      if (!skontoBetragField.value) {
        calculateZahlbetrag(0, isAbweichenderRechnungsBetrag);
      } else {
        calculateZahlbetragWithSkontoBetrag();
      }
    }
  }, [
    skontoInProzentField.value,
    skontoBetragField.value,
    calculateSkontoFromSkontoInProzent,
    calculateZahlbetragWithSkontoBetrag,
    calculateZahlbetrag,
    isAbweichenderRechnungsBetrag,
  ]);

  useEffect(() => {
    if (skontoBetragField.value) {
      calculateZahlbetragWithSkontoBetrag();
    } else {
      calculateZahlbetrag(0, isAbweichenderRechnungsBetrag);
    }
  }, [
    rechnungsBetragChanged,
    isAbweichenderRechnungsBetrag,
    rechnungsbetragField.value,
    bruttobetragField.value,
    skontoBetragField.value,
    calculateZahlbetragWithSkontoBetrag,
    calculateZahlbetrag,
  ]);

  useEffect(() => {
    if (!zahlBetrag) {
      handleSkontoOrZahlbetragCalculation();
    }
  }, [zahlBetrag, handleSkontoOrZahlbetragCalculation]);

  useEffect(() => {
    if (skontoInProzentField.value && !skontoBetrag) {
      calculateSkontoFromSkontoInProzent();
    }
  }, [
    skontoBetrag,
    skontoInProzentField.value,
    calculateSkontoFromSkontoInProzent,
  ]);

  const handleSkontoInProzentChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFieldValue("skonto1InProzent", e.target.value);
    const skontoBetrag = calculateSkontoBetrag(e.target.value);
    calculateZahlbetrag(skontoBetrag, isAbweichenderRechnungsBetrag);
  };

  const handleSkontoBetragChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFieldValue("skontoBetrag", e.target.value);
    const skontoBetrag = parseFloat(
      e.target.value.replace(".", "").replace(",", ".")
    );
    calculateZahlbetrag(skontoBetrag, isAbweichenderRechnungsBetrag);
  };

  return {
    handleSkontoInProzentChange,
    handleSkontoBetragChange,
  };
};
